/**
 * DashboardProjectItem — one project ticket in the dashboard sidebar.
 *
 * Shared by the flat project list (org users) and the per-client panel
 * (platform admin). When `onEdit` is supplied the ticket exposes an inline
 * editor (pencil) for name, location and capacity; Enter/✓ saves, Esc/✕
 * cancels. Only the fields that actually changed are sent, and an emptied
 * location/capacity is sent as null so the server clears it. The parent owns
 * persistence and resolves `true` once saved; `false` keeps the editor open so
 * the admin can retry after an error toast.
 */

import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import type { SiteListItem, UpdateProjectInput, AdminOrganizationItem } from '../../services/projectApi';
import type { AlarmCount } from './DashboardMap';

export interface DashboardProjectItemProps {
  site: SiteListItem;
  counts?: AlarmCount;
  isHovered: boolean;
  isLoading: boolean;
  /** Include the client name in the meta line — flat list only; it is
   *  redundant inside a client panel. */
  showClient: boolean;
  onHoverChange: (id: string | null) => void;
  onSelect: (site: SiteListItem) => void;
  onRequestSetLocation: (site: SiteListItem) => void;
  /** Present only for callers allowed to edit site details (platform admin).
   *  `patch` carries only the changed fields; null clears location/capacity. */
  onEdit?: (site: SiteListItem, patch: UpdateProjectInput) => Promise<boolean>;
  /** Platform admin: organizations a site can be moved to (null = not loaded yet). */
  orgs?: AdminOrganizationItem[] | null;
  /** Present only for platform admins. Resolves true once the site was moved
   *  (the parent refreshes the list); false keeps the panel open for a retry. */
  onMove?: (site: SiteListItem, targetOrgId: string, confirmName: string) => Promise<boolean>;
}

interface Draft {
  name: string;
  location: string;
  capacityKw: string;
}

function draftFrom(site: SiteListItem): Draft {
  return {
    name: site.name,
    location: site.location ?? '',
    capacityKw: site.capacity_kw ? String(site.capacity_kw) : '',
  };
}

/**
 * Build the PUT body from the editor draft — only what differs from the row.
 * `error` is a user-facing validation message. Module-private (a non-component
 * export would break fast refresh); covered by the sidebar tests through the UI.
 */
function buildSitePatch(
  site: Pick<SiteListItem, 'name' | 'location' | 'capacity_kw'>,
  draft: Draft,
): { patch: UpdateProjectInput; error: string | null } {
  const patch: UpdateProjectInput = {};

  const name = draft.name.trim();
  if (!name) return { patch, error: 'Name is required' };
  if (name !== site.name) patch.name = name;

  const location = draft.location.trim();
  if (location !== (site.location ?? '')) patch.location = location || null;

  const capRaw = draft.capacityKw.trim();
  let capacityKw: number | null = null;
  if (capRaw) {
    capacityKw = Number(capRaw);
    if (!Number.isFinite(capacityKw) || capacityKw <= 0) {
      return { patch, error: 'Capacity must be a positive number of kW' };
    }
  }
  if (capacityKw !== (site.capacity_kw ?? null)) patch.capacityKw = capacityKw;

  return { patch, error: null };
}

/** "7 Sep 2026" from a `YYYY-MM-DD` scan day. Parsed at local midnight so the
 *  day never shifts across the UTC boundary. */
function formatDay(day: string): string {
  const d = new Date(`${day}T00:00:00`);
  if (Number.isNaN(d.getTime())) return day;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function DashboardProjectItem({
  site,
  counts,
  isHovered,
  isLoading,
  showClient,
  onHoverChange,
  onSelect,
  onRequestSetLocation,
  onEdit,
  orgs = null,
  onMove,
}: DashboardProjectItemProps) {
  const [editing, setEditing] = useState(false);
  // Move-to-organization panel (admin). Typed-name confirmation like purge;
  // the server re-checks admin role, archived state, name and target org.
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveOrgId, setMoveOrgId] = useState('');
  const [moveConfirm, setMoveConfirm] = useState('');
  const [moveSaving, setMoveSaving] = useState(false);
  const [moveError, setMoveError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>(() => draftFrom(site));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement | null>(null);

  // Guards the post-await setState. Set true in the effect body so
  // StrictMode's mount→unmount→mount rehearsal cannot leave it false.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (!editing) return;
    nameRef.current?.focus();
    nameRef.current?.select();
  }, [editing]);

  const hasPin = typeof site.latitude === 'number' && typeof site.longitude === 'number';
  const isCritical = (counts?.critical ?? 0) > 0;
  const isWarning = !isCritical && (counts?.warning ?? 0) > 0;

  const beginEdit = () => {
    setDraft(draftFrom(site));
    setError(null);
    setMoveOpen(false);
    setEditing(true);
  };

  const beginMove = () => {
    setMoveOrgId('');
    setMoveConfirm('');
    setMoveError(null);
    setEditing(false);
    setMoveOpen(true);
  };

  const cancelMove = () => {
    if (moveSaving) return;
    setMoveOpen(false);
    setMoveError(null);
  };

  const submitMove = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!onMove || moveSaving) return;
    if (!moveOrgId) {
      setMoveError('Choose an organization');
      return;
    }
    if (moveConfirm !== site.name) {
      setMoveError('Type the exact project name to confirm');
      return;
    }
    setMoveError(null);
    setMoveSaving(true);
    const ok = await onMove(site, moveOrgId, moveConfirm);
    if (!mountedRef.current) return;
    setMoveSaving(false);
    if (ok) setMoveOpen(false);
  };

  const handleMoveKeyDown = (e: KeyboardEvent<HTMLInputElement | HTMLSelectElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      cancelMove();
    }
  };

  // Current and deleted organizations are never offered as targets.
  const moveTargets = (orgs ?? []).filter((o) => o.status !== 'deleted' && o.id !== site.org_id);

  const cancelEdit = () => {
    if (saving) return;
    setEditing(false);
    setError(null);
    setDraft(draftFrom(site));
  };

  const submitEdit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!onEdit || saving) return;
    const { patch, error: validation } = buildSitePatch(site, draft);
    if (validation) {
      setError(validation);
      return;
    }
    if (Object.keys(patch).length === 0) {
      setEditing(false);
      return;
    }
    setError(null);
    setSaving(true);
    const ok = await onEdit(site, patch);
    if (!mountedRef.current) return;
    setSaving(false);
    if (ok) setEditing(false);
    else nameRef.current?.focus();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      cancelEdit();
    }
  };

  const setField = (key: keyof Draft) => (e: { target: { value: string } }) =>
    setDraft((d) => ({ ...d, [key]: e.target.value }));

  // Second row: client (flat list only) · location · capacity · last capture.
  // "Last capture" is the newest photo/orthophoto scan day the API found for
  // the site — what an inspector means by "when was this last flown". It is
  // deliberately not updated_at, which only moves on metadata edits.
  // undefined = the dashboard has not received capture dates yet (say
  // nothing); null = nothing dated exists for this site.
  const capture =
    site.last_capture_date === undefined
      ? null
      : site.last_capture_date
        ? `Last capture ${formatDay(site.last_capture_date)}`
        : 'No captures yet';
  const meta =
    [
      showClient ? site.client_name : null,
      site.location,
      site.capacity_kw ? `${site.capacity_kw} kW` : null,
      capture,
    ]
      .filter(Boolean)
      .join(' · ') || ' ';

  return (
    <li
      className={`dashboard-sidebar__item ${isHovered ? 'is-hovered' : ''} ${isLoading ? 'is-loading' : ''} ${editing || moveOpen ? 'is-editing' : ''}`}
      onMouseEnter={() => onHoverChange(site.id)}
      onMouseLeave={() => onHoverChange(null)}
    >
      {editing ? (
        <form className="dashboard-sidebar__edit" onSubmit={submitEdit}>
          <input
            ref={nameRef}
            className="dashboard-sidebar__rename-input"
            value={draft.name}
            onChange={setField('name')}
            onKeyDown={handleKeyDown}
            maxLength={255}
            disabled={saving}
            aria-label="Project name"
            placeholder="Project name"
          />
          <div className="dashboard-sidebar__edit-row">
            <input
              className="dashboard-sidebar__rename-input"
              value={draft.location}
              onChange={setField('location')}
              onKeyDown={handleKeyDown}
              maxLength={500}
              disabled={saving}
              aria-label="Location"
              placeholder="Location"
            />
            <input
              className="dashboard-sidebar__rename-input dashboard-sidebar__edit-capacity"
              value={draft.capacityKw}
              onChange={setField('capacityKw')}
              onKeyDown={handleKeyDown}
              inputMode="decimal"
              disabled={saving}
              aria-label="Capacity (kW)"
              placeholder="kW"
            />
          </div>
          {error && (
            <div className="dashboard-sidebar__edit-error" role="alert">{error}</div>
          )}
          <div className="dashboard-sidebar__edit-actions">
            <button
              type="button"
              className="dashboard-sidebar__rename-btn"
              onClick={cancelEdit}
              disabled={saving}
              title="Cancel"
              aria-label="Cancel edit"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
            <button
              type="submit"
              className="dashboard-sidebar__rename-btn dashboard-sidebar__rename-btn--save"
              disabled={saving || !draft.name.trim()}
              title="Save"
              aria-label="Save details"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </button>
          </div>
        </form>
      ) : moveOpen ? (
        <form
          className="dashboard-sidebar__edit"
          onSubmit={submitMove}
          aria-label={`Move ${site.name} to another organization`}
        >
          <div className="dashboard-sidebar__edit-title">Move “{site.name}” to</div>
          <select
            className="dashboard-sidebar__rename-input"
            value={moveOrgId}
            onChange={(e) => setMoveOrgId(e.target.value)}
            onKeyDown={handleMoveKeyDown}
            disabled={moveSaving || orgs === null}
            aria-label="Target organization"
          >
            <option value="">{orgs === null ? 'Loading organizations…' : 'Select an organization…'}</option>
            {moveTargets.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}{o.plan_id ? ` · ${o.plan_id}` : ''} · {o.site_count} {o.site_count === 1 ? 'site' : 'sites'}
              </option>
            ))}
          </select>
          <input
            className="dashboard-sidebar__rename-input"
            value={moveConfirm}
            onChange={(e) => setMoveConfirm(e.target.value)}
            onKeyDown={handleMoveKeyDown}
            disabled={moveSaving}
            aria-label="Type the project name to confirm"
            placeholder={`Type “${site.name}” to confirm`}
            autoComplete="off"
          />
          <div className="dashboard-sidebar__edit-hint">
            Share and upload links keep working for the new owner. Members of the current organization lose access immediately.
          </div>
          {moveError && (
            <div className="dashboard-sidebar__edit-error" role="alert">{moveError}</div>
          )}
          <div className="dashboard-sidebar__edit-actions">
            <button
              type="button"
              className="dashboard-sidebar__rename-btn"
              onClick={cancelMove}
              disabled={moveSaving}
              title="Cancel"
              aria-label="Cancel move"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
            <button
              type="submit"
              className="dashboard-sidebar__move-btn"
              disabled={moveSaving || !moveOrgId || moveConfirm !== site.name}
              aria-label="Confirm move"
            >
              {moveSaving ? 'Moving…' : 'Move project'}
            </button>
          </div>
        </form>
      ) : (
        <>
          <button
            className="dashboard-sidebar__item-main"
            onClick={() => onSelect(site)}
            disabled={isLoading}
          >
            <div className="dashboard-sidebar__item-row">
              <span className="dashboard-sidebar__item-name">{site.name}</span>
              {isCritical && (
                <span className="dashboard-sidebar__badge dashboard-sidebar__badge--critical" title={`${counts!.critical} critical · ${counts!.warning} warning`}>
                  {counts!.critical + counts!.warning}
                </span>
              )}
              {isWarning && (
                <span className="dashboard-sidebar__badge dashboard-sidebar__badge--warning" title={`${counts!.warning} warning`}>
                  {counts!.warning}
                </span>
              )}
            </div>
            <div className="dashboard-sidebar__item-meta">{meta}</div>
          </button>
          {onEdit && (
            <button
              type="button"
              className="dashboard-sidebar__item-action"
              onClick={(e) => { e.stopPropagation(); beginEdit(); }}
              title="Edit name, location, capacity"
              aria-label={`Edit ${site.name}`}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 20h9" />
                <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
              </svg>
            </button>
          )}
          {onMove && (
            <button
              type="button"
              className="dashboard-sidebar__item-action"
              onClick={(e) => { e.stopPropagation(); beginMove(); }}
              title="Move to another organization"
              aria-label={`Move ${site.name}`}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h6v6" />
                <path d="M20 20h-6v-6" />
                <path d="M4 20l16-16" />
              </svg>
            </button>
          )}
          {!hasPin && (
            <button
              className="dashboard-sidebar__set-location"
              onClick={(e) => { e.stopPropagation(); onRequestSetLocation(site); }}
              title="Pin this project on the map"
            >
              Set location
            </button>
          )}
        </>
      )}
    </li>
  );
}
